import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

const KEYS = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

// Accessible tablists, ported from the approved page scripts.
const wireTabs = (root) => {
  const cleanups = [];
  root.querySelectorAll('[role="tablist"]').forEach((list) => {
    const tabs = [...list.querySelectorAll('[role="tab"]')];
    const panelOf = (t) => root.querySelector(`#${t.getAttribute('aria-controls')}`);
    const select = (i, focus) => {
      tabs.forEach((t, j) => {
        t.setAttribute('aria-selected', String(i === j));
        t.tabIndex = i === j ? 0 : -1;
        const p = panelOf(t);
        if (p) p.hidden = i !== j;
      });
      if (focus) tabs[i].focus();
    };
    tabs.forEach((t, i) => {
      const onClick = () => select(i);
      const onKey = (e) => {
        let n;
        if (KEYS[e.key]) n = (i + KEYS[e.key] + tabs.length) % tabs.length;
        if (e.key === 'Home') n = 0;
        if (e.key === 'End') n = tabs.length - 1;
        if (n !== undefined) { e.preventDefault(); select(n, true); }
      };
      t.addEventListener('click', onClick);
      t.addEventListener('keydown', onKey);
      cleanups.push(() => { t.removeEventListener('click', onClick); t.removeEventListener('keydown', onKey); });
    });
  });
  return () => cleanups.forEach((c) => c());
};

// Scroll reveal, ported from the approved page scripts (skipped under reduced motion).
const wireReveal = (root, reveal) => {
  if (!reveal) return () => {};
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches || !('IntersectionObserver' in window)) return () => {};
  const items = [...root.querySelectorAll(reveal.selector)];
  const io = new IntersectionObserver((entries) => entries.forEach((en) => {
    if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); }
  }), reveal.options);
  items.forEach((el) => { el.classList.add(reveal.readyClass); io.observe(el); });
  const onChange = (e) => { if (e.matches) { items.forEach((el) => el.classList.add('is-visible')); io.disconnect(); } };
  reduced.addEventListener('change', onChange);
  return () => { io.disconnect(); reduced.removeEventListener('change', onChange); };
};

const setMeta = ({ title, description }) => {
  document.title = title;
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) { tag = document.createElement('meta'); tag.name = 'description'; document.head.appendChild(tag); }
  tag.content = description;
};

/** Renders an approved page body verbatim and wires its interactions. `slots` maps element ids to React nodes. */
export const ApprovedPage = ({ slug, content, reveal, slots = {} }) => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const [targets, setTargets] = useState({});
  const markup = useMemo(() => ({ __html: content.HTML }), [content]);

  useLayoutEffect(() => {
    const found = {};
    Object.keys(slots).forEach((id) => { found[id] = ref.current.querySelector(`#${id}`); });
    setTargets(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  useEffect(() => {
    const root = ref.current;
    setMeta(content.META);
    const offTabs = wireTabs(root);
    const offReveal = wireReveal(root, reveal);
    const onClick = (e) => {
      const a = e.target.closest('a[href]');
      if (!a || a.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      const href = a.getAttribute('href');
      if (href.startsWith('#')) {
        e.preventDefault();
        root.querySelector(href)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (href.startsWith('/')) {
        e.preventDefault();
        navigate(href);
      }
    };
    root.addEventListener('click', onClick);
    return () => { offTabs(); offReveal(); root.removeEventListener('click', onClick); };
  }, [content, reveal, navigate]);

  return (
    <>
      <div ref={ref} className={`gp-${slug}`} data-testid={`approved-page-${slug}`} dangerouslySetInnerHTML={markup} />
      {Object.entries(slots).map(([id, node]) => (targets[id] ? createPortal(node, targets[id], id) : null))}
    </>
  );
};
